import {
  ChartConfigurationSettings,
  ErrorBandsConfig,
  ErrorBandsTemplateConfig,
  getErrorBandsConfigByMetricNameHandlingLegacyKeyNames,
  getXAxisModeHandlingLegacyCustomExpressionAsStepMode,
  JsonCompatibleAxisRange,
  LegacyErrorBandsConfig,
  normalizeErrorBandsTemplateConfig,
  OutliersDisplayMode,
  ReferenceLineConfigById,
  removeNullsFromAxisRange,
} from '@neptune/charts-domain';
import { ParsedExpression } from '@neptune/shared/custom-expressions-domain';
import { SmoothingAlgorithm } from '@neptune/shared/smoothing-domain';
import type {
  AxisRange,
  AxisScale,
  Widget,
  WidgetDefinitionNew,
  XAxisMode,
} from '@neptune/shared/widgets-domain';

import { Complete } from 'common/utility-types';
import { AttributeDefinition } from 'domain/experiment/attribute';

import { WidgetDefinition } from '../core/domain/widget-definition';

export type ChartWidget = Widget & {
  type: 'chart';
  options: ChartWidgetOptions;
};

export type CustomExpression = { alias?: string; expression: string };

export type ExpressionSourceEntry = {
  id: string;
  enabled: boolean;
  alias?: string;
  expression: string;
  parsedExpression: ParsedExpression;
};

export function isValidExpressionEntry(entry: ExpressionSourceEntry) {
  return (
    entry.parsedExpression.status === 'valid' && entry.parsedExpression.attributes.length !== 0
  );
}

export type ChartWidgetOptions = {
  averaging?: boolean;
  xAxisScale?: AxisScale;
  yAxisScale?: AxisScale;
  xAxisMode?: XAxisMode;
  xAxisMetric?: AttributeDefinition;
  yAxisRange?: AxisRange;
  xAxisRange?: AxisRange;
  metricsStepsRange?: AxisRange;
  customXExpression?: string;
  smoothing?: number;
  showCompletion?: boolean;
  showOriginalLineOnSmoothing?: boolean;
  smoothingAlgorithm?: SmoothingAlgorithm;
  customXExpressionName?: string;
  normalizeToFirstValue?: boolean;
  // Custom error bands
  customErrorBandsEnabled?: boolean;
  customErrorBandsConfigByMetricName?: Record<string, ErrorBandsConfig>;
  // Applies to series matched by the attribute-pattern source that are not selected individually.
  customErrorBandsConfigForAttributePattern?: ErrorBandsTemplateConfig;
  // Reference lines
  referenceLinesEnabled?: boolean;
  referenceLinesConfigById?: ReferenceLineConfigById;
  outliersDisplayMode?: OutliersDisplayMode;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function compatibilityCheck(
  ChartWidgetOptions: ChartWidgetOptions,
  PartialChartConfigurationSettings: Partial<ChartConfigurationSettings>,
): {
  ChartWidgetOptions: ChartWidgetOptions;
  PartialChartConfigurationSettings: Partial<ChartConfigurationSettings>;
} {
  // Verify that ChartWidgetOptions are compatible with Partial<ChartConfigurationSettings>.
  // This is the type we want them to be, but as ChartWidgetOptions are stored in the
  // DB (with the conversion defined below, but it's weakly typed) we need to make
  // sure that changes to it are made with required care.
  // NOTE: This verification allows field to be added to ChartConfigurationSettings
  // without adding them to ChartWidgetOptions, but not the other way around.
  return {
    ChartWidgetOptions: PartialChartConfigurationSettings,
    PartialChartConfigurationSettings: ChartWidgetOptions,
  };
}

export function isChartWidget(widget: Widget): widget is ChartWidget {
  return widget.type === 'chart';
}

export type ApiChartWidgetOptions = {
  averaging?: boolean;
  smoothing?: number;
  showCompletion?: boolean;
  showOriginalLineOnSmoothing?: boolean;
  smoothingAlgorithm?: 'emaCausal' | 'gaussianCentered' | 'smaCausal' | 'smaCentered';
  xAxisScale?: AxisScale;
  yAxisMode?: AxisScale;
  xAxisMode?: XAxisMode;
  xAxisMetric?: AttributeDefinition;
  yAxisRange?: JsonCompatibleAxisRange;
  xAxisRange?: JsonCompatibleAxisRange;
  metricsStepsRange?: JsonCompatibleAxisRange;
  customXExpressionName?: string;
  customXExpression?: string;
  // @deprecated Replaced by previewPoints. Kept to support old widgets saved before the change.
  normalizeToFirstValue?: boolean;
  customErrorBandsEnabled?: boolean;
  customErrorBandsConfigByMetricName?: Record<string, ErrorBandsConfig | LegacyErrorBandsConfig>;
  customErrorBandsConfigForAttributePattern?: ErrorBandsTemplateConfig;
  // @deprecated Ignored when reading older saved widgets; new widgets do not persist this field.
  errorBandJitterEnabled?: boolean;
  referenceLinesEnabled?: boolean;
  referenceLinesConfigById?: ReferenceLineConfigById;
  outliersDisplayMode?: OutliersDisplayMode;
};

export function chartWidgetOptionsFromDomainToApi({
  averaging,
  xAxisScale,
  yAxisScale,
  xAxisMode,
  xAxisMetric,
  yAxisRange,
  metricsStepsRange,
  customXExpression,
  customXExpressionName,
  xAxisRange,
  smoothing,
  showCompletion,
  showOriginalLineOnSmoothing,
  smoothingAlgorithm,
  normalizeToFirstValue,
  customErrorBandsEnabled,
  customErrorBandsConfigByMetricName,
  customErrorBandsConfigForAttributePattern,
  referenceLinesEnabled,
  referenceLinesConfigById,
  outliersDisplayMode,
}: ChartWidgetOptions): Complete<Omit<ApiChartWidgetOptions, 'errorBandJitterEnabled'>> {
  return {
    // List all fields explicitly to avoid saving any extra fields that might be passed to converter.
    averaging,
    xAxisScale,
    xAxisMode,
    yAxisMode: yAxisScale,
    xAxisMetric,
    yAxisRange,
    xAxisRange,
    metricsStepsRange,
    customXExpression,
    customXExpressionName,
    normalizeToFirstValue,
    smoothing,
    showCompletion,
    /** @ts-expect-error @deprecated use showCompletion instead, we keep it to make rollback easy */
    // eslint-disable-next-line no-nested-ternary
    previewPoints: showCompletion === undefined ? undefined : showCompletion ? 'on' : 'off',
    showOriginalLineOnSmoothing,
    smoothingAlgorithm,
    customErrorBandsEnabled,
    customErrorBandsConfigByMetricName,
    customErrorBandsConfigForAttributePattern,
    referenceLinesEnabled,
    referenceLinesConfigById,
    outliersDisplayMode,
  };
}

export function chartWidgetOptionsFromApiToDomain(
  options?: ApiChartWidgetOptions,
): Complete<ChartWidgetOptions> {
  // Backward compatibility for persisted legacy shape.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore this field is not used anymore
  const previewPoints = options?.previewPoints;

  return {
    xAxisMetric: options?.xAxisMetric,
    xAxisMode: getXAxisModeHandlingLegacyCustomExpressionAsStepMode(options),
    xAxisScale: options?.xAxisScale,
    yAxisScale: options?.yAxisMode,
    averaging: options?.averaging,
    smoothing: options?.smoothing,
    showCompletion: options?.showCompletion ?? (previewPoints ? previewPoints === 'on' : undefined),
    showOriginalLineOnSmoothing: options?.showOriginalLineOnSmoothing,
    smoothingAlgorithm: options?.smoothingAlgorithm,
    yAxisRange: removeNullsFromAxisRange(options?.yAxisRange),
    metricsStepsRange: removeNullsFromAxisRange(options?.metricsStepsRange),
    customXExpression: options?.customXExpression,
    customXExpressionName: options?.customXExpressionName,
    xAxisRange: removeNullsFromAxisRange(options?.xAxisRange),
    normalizeToFirstValue: options?.normalizeToFirstValue,
    customErrorBandsEnabled: options?.customErrorBandsEnabled,
    customErrorBandsConfigByMetricName: getErrorBandsConfigByMetricNameHandlingLegacyKeyNames(
      options?.customErrorBandsConfigByMetricName,
    ),
    customErrorBandsConfigForAttributePattern: normalizeErrorBandsTemplateConfig(
      options?.customErrorBandsConfigForAttributePattern,
    ),
    referenceLinesEnabled: options?.referenceLinesEnabled,
    referenceLinesConfigById: options?.referenceLinesConfigById,
    outliersDisplayMode: options?.outliersDisplayMode,
  };
}

export const chartWidgetDefinition: WidgetDefinition = {
  type: 'chart',
  fromApiToDomain: chartWidgetOptionsFromApiToDomain,
  fromDomainToApi: chartWidgetOptionsFromDomainToApi,
};

// todo: export in api layer after move to chart-alike silo
export const chartWidgetDefinitionNew: WidgetDefinitionNew = {
  widgetType: 'chart' as const,
  label: 'Chart',
  description: 'Line graph to visualize metrics against time',
  icon: 'chart-line',
  dataItem: 'chart',
  hasSettings: true,
};
